import { ReportDefinition } from '../Settings/reportDsl';

/**
 * Resolves the level-2 panels that should open when a row is clicked.
 *
 * Driven entirely by the source report's suite DSL (`onRowClick`):
 *   - `level2ReportNames` → those reports, in listed order (stacked sibling
 *     L2 tables such as uploaded / excluded / already-synced).
 *   - otherwise the single `targetReportId` / `targetReportName`.
 *
 * Used by both the chrome Reports page and the chrome-less shareable /
 * Direct Reports viewer so drill-down stays in one place.
 */
export const resolveLinkedL2Reports = (
  sourceReport: ReportDefinition,
  allReports: ReportDefinition[],
  drilldownReports: ReportDefinition[],
): ReportDefinition[] => {
  const cfg = sourceReport.interactions?.onRowClick;
  if (!cfg) return [];

  const lookup = (name: string) =>
    allReports.find((r) => r.name === name)
    || drilldownReports.find((r) => r.name === name);

  const names = cfg.level2ReportNames;
  if (names && names.length > 0) {
    const resolved = names
      .map((n) => lookup(n))
      .filter((r): r is ReportDefinition => Boolean(r));
    if (resolved.length > 0) return resolved;
  }

  const target = cfg.targetReportId
    ? drilldownReports.find((r) => r._id === cfg.targetReportId)
      || allReports.find((r) => r._id === cfg.targetReportId)
    : (cfg.targetReportName ? lookup(cfg.targetReportName) : undefined);

  return target ? [target] : [];
};

/**
 * Resolves the panels shown when a dashboard counter is clicked.
 *
 * Driven by the source report's `onPanelClick`:
 *   - `level2ReportNames` → those drill-down reports, in listed order
 *     (e.g. one counter per AEM environment).
 *   - otherwise the single `targetReportId` / `targetReportName`.
 */
export const resolveLinkedPanelReports = (
  sourceReport: ReportDefinition,
  allReports: ReportDefinition[],
  drilldownReports: ReportDefinition[],
): ReportDefinition[] => {
  const cfg = sourceReport.interactions?.onPanelClick;
  if (!cfg) return [];

  const lookup = (name: string) =>
    allReports.find((r) => r.name === name)
    || drilldownReports.find((r) => r.name === name);

  const names = cfg.level2ReportNames;
  if (names && names.length > 0) {
    const resolved = names
      .map((n) => lookup(n))
      .filter((r): r is ReportDefinition => Boolean(r));
    if (resolved.length > 0) return resolved;
  }

  const target = cfg.targetReportId
    ? drilldownReports.find((r) => r._id === cfg.targetReportId)
      || allReports.find((r) => r._id === cfg.targetReportId)
    : (cfg.targetReportName ? lookup(cfg.targetReportName) : undefined);

  return target ? [target] : [];
};
